import type { ShownChannelJoinInvite } from 'state/channelJoinInvite'
import { createWithEqualityFn } from 'zustand/traditional'
import { shallow } from 'zustand/shallow'
import { current, produce, enablePatches, produceWithPatches, applyPatches } from 'immer'

import api from 'api/api'
import { Quest } from 'types/quests'
import { Answer } from 'types/answers'
import { User } from 'types/users'

import { BsvWalletType } from 'wallets/wallets'
import * as cache from 'state/cache'
import { Space } from 'types/spaces'

import { emptyPanel, PanelState } from 'state/PanelState'
import { userHasFeature } from 'refactor/hooks/useUserHasFeature'
import { getCenterIndex, getSwiperRef } from 'state/imperativeApis/swiperApi'
import { transformTo, BIG, SMALL } from 'lib/transform'
import { SATOSHI_UNIT, BITCOIN_UNIT, type BsvUnit } from 'lib/bsv-util'
import { SignalType } from 'types/signals'
import { isMobile } from 'lib/device'
import {
    capturePanelRow,
    nextPanelRowShiftToken,
    type PanelBox,
    type PanelRowShift,
} from 'lib/panelRowShift'
import { IAiModel } from './aiModels'
import type { ActionDefinition } from './actionDefinitions'
import { type AgentModelConfig } from './agentModelConfig'
import { THEME_STORAGE_KEY } from '../constants/theme'
import { Filter } from 'components/filters/useStreamFilters'
import { stopActiveMediaPlayback } from 'state/mediaPlaybackCoordinator'
import { setPlayThreadHash } from 'utils'

export const LOCAL_STORAGE_DEBUG_KEY = 'TREECHAT_LOCAL_DEBUG'
export const DEBUG_MODE_CHANGED_EVENT = 'treechat:debug-mode-changed'
export const DEFAULT_AGENT_MODEL_ID = 'anthropic/claude-sonnet-5.5'
export type AuthModalMode = 'signup' | 'login'
export type AuthModalSource = string

const DEFAULT_NUM_PANELS =
    typeof window !== 'undefined'
        ? window.gon?.currentUser?.space_options?.numPanels ?? (window.gon?.currentUser ? 3 : 1)
        : 3

type JobStatus = {
    status?: string
    substatus?: string
    data?: Record<string, unknown>
    messages?: string[]
    run_id?: string
    timestamp?: number
    started_at?: number
    finished_at?: number
}

export type PendingUpvalueOp = {
    id: string
    sats: number
    isBoost: boolean
    isSeed?: boolean
    reactionEmoji?: string
    sentimentSource?: string
    createdAt: string
    observedAnswerSnapshotSha256?: string
    // Present when the press originated on the artifact surface (ordinals
    // stream dock) — routes the payout to the ordinal's current owner.
    artifactOrdinalId?: string
    // Dynamic playlist: the song answer that was playing when the press
    // happened — credits the song and posts "@user is playing …".
    nowPlayingAnswerId?: string
}

export type PendingOptimisticAnswerRef = {
    answerId: string
    createdAtMs: number
}

export type AppearanceTheme = 'light' | 'dark' | 'system'
type UserFeature = string | NonNullable<User['features']>[number]
export type GlobalNoticeVariant = 'error' | 'success' | 'info' | 'pending' | 'bitcoin' | 'usd'
export type GlobalNotice = {
    id: string
    message: string
    detail?: string
    variant: GlobalNoticeVariant
    durationMs: number
    kind?: string
    replaceKind?: boolean
    testId?: string
    actionLabel?: string
    cancelLabel?: string
    onAction?: () => void
}
// Returns the current system preference (light or dark)
export const getSystemThemePreference = (): 'light' | 'dark' => {
    if (typeof window === 'undefined') {
        return 'light'
    }
    // Check for native mobile app theme first (injected by Expo)
    const nativeTheme = window.NATIVE_SYSTEM_THEME
    if (nativeTheme === 'light' || nativeTheme === 'dark') {
        return nativeTheme
    }
    // Fall back to browser media query
    const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches
    return prefersDark ? 'dark' : 'light'
}

// Resolves the actual visual theme to apply (always light or dark, never 'system')
export const resolveEffectiveTheme = (theme: AppearanceTheme): 'light' | 'dark' => {
    if (theme === 'system') {
        return getSystemThemePreference()
    }
    return theme
}

const normalizeAppearanceTheme = (value?: string | null): AppearanceTheme | null => {
    if (!value) {
        return null
    }

    if (value === 'light' || value === 'dark' || value === 'system') {
        return value
    }

    if (value.endsWith('-light')) {
        return 'light'
    }

    if (value.endsWith('-dark')) {
        return 'dark'
    }

    return null
}

const resolveInitialAppearanceTheme = (): AppearanceTheme => {
    if (typeof window === 'undefined') {
        return 'light'
    }

    const serverTheme = normalizeAppearanceTheme(window.gon?.currentUser?.space_options?.theme)
    if (serverTheme) {
        return serverTheme
    }

    const storedTheme = normalizeAppearanceTheme(window.localStorage?.getItem(THEME_STORAGE_KEY))
    if (storedTheme) {
        return storedTheme
    }

    const rootTheme =
        typeof document !== 'undefined'
            ? document.documentElement?.getAttribute('data-theme')
            : null
    const normalizedRootTheme = normalizeAppearanceTheme(rootTheme)
    if (normalizedRootTheme) {
        return normalizedRootTheme
    }

    return 'system'
}

const DEFAULT_APPEARANCE_THEME: AppearanceTheme = resolveInitialAppearanceTheme()

/** Callback for handling video recordings — receives a File or Blob from video/screen recorders */
export type VideoHandler = (video: File | Blob) => void

/** Action recorded after a panel insert or remove operation.
 *
 *  Insert shape: { insert: PanelState, appendSession?, openCenter?, prevActiveIndex?, ... }
 *  Remove shape: { remove: PanelState, removeIndex, animated, centerIndex, panelIds, suppressSlide? }
 *
 *  Action params from orchestrators are spread into the object, so the type
 *  uses an index signature for the extra keys that flow through. */
export type PanelLastAction = {
    // Insert fields
    insert?: PanelState
    appendSession?: boolean
    // Remove fields
    remove?: PanelState
    removeIndex?: number
    animated?: 'left' | 'right' | false
    centerIndex?: number
    panelIds?: string[]
    suppressSlide?: boolean
    // openCenter flow params (spread from actionParams by orchestrators)
    openCenter?: boolean
    prevActiveIndex?: number
    removeLeftIndex?: number
    animateRemove?: boolean
    insertAtIndex?: number
    /** Quest ID passed by orchestrators for SearchHeader/UserProfile panel detection */
    questId?: string
    // Orchestrators may pass additional keys
    [key: string]: unknown
} | null

/** State for an animated panel insertion in progress */
export type AnimatedPanelInsert = {
    index: number
    panel: Partial<PanelState>
    direction: 'left' | 'right'
    actionParams?: Record<string, unknown>
} | null

/** State for an animated panel removal in progress */
export type AnimatedPanelRemove = {
    index: number
    direction: 'left' | 'right'
} | null

export interface IKnovStore {
    DEBUG: boolean
    panels: {
        state: PanelState[]
        lastAction: PanelLastAction
        animatedPanelInsert: AnimatedPanelInsert
        animatedPanelRemove: AnimatedPanelRemove
        /** Desktop three-panel insert being animated in place (lib/panelRowShift) */
        rowShift: PanelRowShift | null
        getPanels: () => PanelState[]
        getPanel: (panelId: string) => PanelState | undefined
        getIndexById: (panelId: string) => number
        getPanelToRight: (panelId: string) => PanelState | undefined
        getPanelToLeft: (panelId: string) => PanelState | undefined
        editPanel: (panelId: string, newPanelValues: Partial<PanelState>) => void
        insertPanelAtIndex: (
            index: number,
            newPanel: Partial<PanelState>,
            actionParams?: object,
        ) => string | undefined
        removePanelAtIndex: (removeIndex: number) => void
        removePanelAtIndexNoSlide: (removeIndex: number) => void
        startAnimatedInsert: (
            index: number,
            newPanel: Partial<PanelState>,
            options: { direction: 'left' | 'right'; actionParams?: object },
        ) => void
        completeAnimatedInsert: () => void
        startAnimatedRemove: (index: number, direction: 'left' | 'right') => void
        completeAnimatedRemove: () => void
        insertPanelRight: (
            panelId: string,
            newPanel: Partial<PanelState>,
            options?: { animate: boolean; actionParams?: object },
        ) => string | undefined
        insertPanelLeft: (
            panelId: string,
            newPanel: Partial<PanelState>,
            options?: { animate: boolean },
        ) => void
        clearPanel: (panelId: string) => void
        appendEmptyPanel: () => void
        prependEmptyPanel: () => void
        appendSession: (newFilter: Filter, panelProps?: Partial<PanelState>) => void
    }
    /** Number of unread notifications */
    numNoti: number
    /** Active swiper index */
    activeIndex: number
    /** Destination content mounted before an animated desktop one-panel transition. */
    onePanelPrewarmIndex: number | null
    /** Whether a touch-driven prewarm destination should remain visible during the gesture. */
    onePanelPrewarmVisible: boolean
    /** Center panel index */
    centerIndex: number
    /** Quest history for the current space */
    questHistory: Quest[]
    /** Knovigator space object */
    knovigatorSpace: Space | null
    /** Public user for unauthenticated views */
    publicUser: User | null
    /** Active space ID */
    activeSpaceId: string | null
    /** Loading space ID (during space transitions) */
    loadingSpaceId: string | null
    /** User features from gon.currentUser.features, with string support for normalized callers */
    userFeatures: UserFeature[] | null
    /** UTC time in ms since cache was hydrated from localstorage */
    lastLocalCacheRead: number
    /** Cached answers live here */
    answers: { [answerId: string]: Answer }
    /** Cached quests live here */
    quests: { [questId: string]: Quest }
    users: User[]
    userSpaces: Space[]
    userStreams: ITeam[]
    lockedQuests: string[]
    lockedAnswers: string[]
    lockQuest: (questId: number) => void
    unlockQuest: (questId: number) => void
    lockAnswer: (answerId: number) => void
    unlockAnswer: (answerId: number) => void
    sliding: boolean
    slidingBack: boolean
    middlePanelExpandedQuests: { [questId: string]: boolean }
    rightPanelExpandedQuests: { [questId: string]: boolean }
    selectedAnswers: { [panelId: string]: { answerId: string } | null }
    selectedQuote: Answer | null
    selectedQuoteParentAnswer: Answer | null
    searchHeaderAnimationStates: { [panelId: string]: { state?: 'fadeIn' | 'fadeOut' | string } }
    hideMiddlePanelExcept: string | null
    hideRightPanelExcept: string | null
    middleNewQuestInput: string
    newAnswerQueryMiddle: string | null
    newAnswerQueryRight: string | null
    unactedAnswers: string[]
    unactedParents: string[]
    starredQuests: Quest[]
    /** Map of quest IDs to starred status */
    starredQuestIds: { [questId: string]: boolean }
    /** Map of team IDs to pinned status */
    pinnedTeamIds: { [teamId: string]: boolean }
    draftQuestIds: { [panel: string]: string | null }
    currentUserBsvWalletType: BsvWalletType | null
    /** Middle panel quest */
    middleQuest: Quest | null
    /** Middle panel quests array */
    middleQuests: Quest[]

    upvalueSats: number | null
    upvalueUsd: number | null
    seedSats: number | null
    seedUsd: number | null
    boostSats: number | null
    effectEnabled: boolean

    answerJobs: { [answerId: string]: JobStatus }
    bsvUnit: BsvUnit

    showSidebar: boolean
    setShowSidebar: (show: boolean, options?: { persist?: boolean }) => void
    closeSidebarOnBlur: boolean
    setCloseSidebarOnBlur: (close: boolean) => void
    closeSidebarOnSwipe: boolean
    setCloseSidebarOnSwipe: (close: boolean) => void
    showRightSidebar: boolean
    setShowRightSidebar: (show: boolean) => void
    numPanels: number
    setNumPanels: (numPanels: number) => void

    getSpace: (spaceId: string) => Space | undefined
    getActiveSpace: () => Space | undefined
    setActiveSpace: (spaceId: string, space: Space) => void
    setActiveHistoryTab: (tab: 'history' | 'starred') => void
    setUserSpaces: (spaces: Space[]) => void
    setUserStreams: (streams: ITeam[]) => void
    set: (
        funcOrObj: ((state: IKnovStore) => void) | Partial<IKnovStore> | Record<string, unknown>,
        callback?: () => void,
    ) => void
    get: () => IKnovStore
    getSpaceId: () => string | undefined
    deleteEmbed: (quest: Quest, answer: Answer, embedId: string) => Promise<void>
    updateQuest: (quest: Quest) => void
    getQuestState: (questId: string) => Quest | undefined
    updateQuestAnswer: (quest: Quest, answer: Answer) => Quest | undefined
    mergeAnswers: (containerQuestId: string, embeddedQuestId: string) => Promise<void>
    setVideoDownloadInProgress: (inProgress: boolean) => Promise<void>
    pinQuestToTeam: (teamId: string, quest: Quest) => Promise<void>
    unpinQuestFromTeam: (teamId: string, quest: Quest) => Promise<void>
    actions: {
        modalActions: {
            openAuthModal: (mode?: AuthModalMode, source?: AuthModalSource) => void
            openWalletModal: (params?: Record<string, unknown>) => void
            openDeviceTransferModal: (params?: Record<string, unknown>) => void
            openOrdinalsModal: (ordinalId?: string) => void
            openLeaderboardModal: (
                category?:
                    | 'given'
                    | 'received'
                    | 'boosted'
                    | 'posters'
                    | 'ai_fees'
                    | 'minters'
                    | 'sharers',
                date?: string,
            ) => void
            openTdpViewerModal: () => void
            openMetricsModal: () => void
            openAdminModal: () => void
            openHostDealModal: (params?: { sharePercent?: number; infoOnly?: boolean }) => void
            openHostStreamPickerModal: (params?: { sharePercent?: number }) => void
            openEditProfileModal: () => void
            openStreamSettingsModal: (team: ITeam, panelId: string, filter: Filter) => void
            openCreateStreamModal: (panelId: string, filter: Filter) => void
            openNewMusicModal: (params: {
                file: File
                kind: 'audio' | 'video'
                initial: { title: string; description: string; cover: File | null }
                onSubmit: (form: { title: string; description: string; cover: File | null }) => void
                onCancel: () => void
            }) => void
            openImportTextModal: () => Promise<void>
            openExportQuestModal: (quest: Quest) => Promise<void>
            openNewStreamModal: () => void
            openNewSpaceModal: () => void
            openVideoRecorderModal: (
                quest: Quest,
                videoHandler: VideoHandler,
                answer: Answer,
            ) => void
            openScreenRecorderModal: (
                quest: Quest,
                videoHandler: VideoHandler,
                answer: Answer,
            ) => void
            openHandCashErrorModal: (msg: string) => void
            openMessageModal: (msg: string) => void
            openConfirmModal: (
                message: string | React.ReactNode,
                confirmLabel: string,
                confirm: () => void,
                options?: Record<string, unknown>,
            ) => void
            openStreamImageUpdateModal: (team: ITeam, callback: () => void) => void
            openSpaceImageUpdateModal: (space: Space, callback: () => void) => void
            openUserImageUpdateModal: (user: User, callback: () => void) => void
            closeModal: () => void
            closeCryptoModal: () => void
            openCryptoModal: (left: number, top: number, callback?: () => void) => void
            openStoryModal: (quest: Quest) => void
            closeStoryModal: () => void
        }
        trayActions: {
            openConfirmTray: (
                message: string | React.ReactNode,
                confirmLabel: string,
                confirm: (reason?: string) => void | boolean | Promise<void | boolean>,
                options?: Record<string, unknown>,
            ) => void
            closeTray: () => void
        }
    }
    isModalOpen: boolean
    isStoryOpen?: boolean
    modalParams: {
        type: string
        title?: string
        icon?: string
        noHeader?: boolean
        customStyles?: React.CSSProperties
        /** Modal implementations pass additional props specific to each modal type */
        [key: string]: unknown
    }
    isTrayOpen: boolean
    trayParams: {
        type: string
        [key: string]: unknown
    } | null
    pushUnactedIds: (ids: string[]) => void
    pushUnactedParentIds: (ids: string[]) => void
    signalType: SignalType
    publicTeams: ITeam[]
    aiModels: IAiModel[]
    actionDefinitions: ActionDefinition[]
    exchangeRate: number
    agentModelConfig: AgentModelConfig | null
    pendingUpvaluesByAnswer: Record<string, PendingUpvalueOp[]>
    upvalueQueueStatus: Record<string, 'idle' | 'processing'>
    enqueuePendingUpvalue: (answerId: string, op: PendingUpvalueOp) => void
    clearPendingUpvalues: (answerId: string, opIds: string[]) => void
    setUpvalueQueueStatus: (answerId: string, status: 'idle' | 'processing') => void
    pendingOptimisticAnswersByQuest: Record<string, PendingOptimisticAnswerRef[]>
    trackPendingOptimisticAnswer: (questId: string, answerId: string) => void
    resolvePendingOptimisticAnswer: (questId: string, answerId: string) => void
    globalNotice: GlobalNotice | null
    // Invite to join a channel after posting in it (state/channelJoinInvite).
    channelJoinInvite: ShownChannelJoinInvite | null
    showGlobalNotice: (
        message: string,
        options?: {
            variant?: GlobalNoticeVariant
            durationMs?: number
            kind?: string
            replaceKind?: boolean
            testId?: string
            actionLabel?: string
            cancelLabel?: string
            onAction?: () => void
            detail?: string
        },
    ) => void
    // Patch the current notice in place when it matches `kind` (preserves id, so the
    // drop-in animation doesn't replay). Used for the pending -> success/error lifecycle.
    updateGlobalNotice: (
        kind: string,
        message: string,
        options?: {
            variant?: GlobalNoticeVariant
            durationMs?: number
            testId?: string
            detail?: string
            actionLabel?: string
            cancelLabel?: string
            onAction?: () => void
        },
    ) => void
    clearGlobalNotice: (id?: string) => void
    appearanceTheme: AppearanceTheme
    setAppearanceTheme: (theme: AppearanceTheme) => void
    /** Per-quest AI agent model selections, keyed by quest ID (hydrated by StateProvider) */
    questAgentModels?: Record<string, string | null>
    /** Whether any answer menu is currently open (prevents swipe during menus) */
    anyMenuOpen?: boolean
    /** Per-space unread notification counts, keyed by space ID (hydrated by useInitNotifications) */
    spaceNotiCounts?: Record<string, number>
    /** Per-quest pending AI-reply status, keyed by quest ID (hydrated by StateProvider) */
    agentReplyPendingStatus?: Record<string, string | boolean>
    /** Whether the crypto pay modal is currently open */
    isCryptoPayModalOpen?: boolean
    /** Left position for the crypto pay modal */
    cryptoPayModalLeft?: number
    /** Top position for the crypto pay modal */
    cryptoPayModalTop?: number
    /** Callback invoked when crypto pay modal confirms a value */
    cryptoPayCallback?: ((value: string) => void) | null
    /** BSV address of the current user (legacy field, set dynamically) */
    currentUserBsvAddress?: string
}

export interface ITeam {
    int_id: number
    name: string
    description?: string | null
    int_admin_id: number
    avatar: string | null
    created_at: string
    updated_at: string
    team_image: {
        url: string
    } | null
    int_quest_id: string | null
    notify_period: string
    last_notify: string | null
    next_notify: string
    public: boolean
    standing_invitations_allowed?: boolean
    private?: boolean
    int_space_id: number
    id: string
    space_id: string
    admin_id: string
    quest_id: string | null
    short_name: string
    path: string
    url: string
    last_activity_at?: string | null

    // StreamSettings perms (nullable => treated as "anyone")
    who_can_post_threads?: string | null
    who_can_reply_threads?: string | null
    show_root_threads_only?: boolean
    thread_display_mode?: 'default' | 'roots_only' | 'all_branches' | null
    seeding_mode?: 'upvalue_only' | 'user_choice' | 'force_seed'
    seed_selected?: boolean
    host_revenue_enabled?: boolean
    private_stub?: boolean
    users_teams?:
        | {
              user_id?: string
              status?: string
              hidden?: boolean
          }
        | Array<{
              user_id?: string
              status?: string
              hidden?: boolean
          }>
    users?: Array<{
        id?: string | number
        name?: string
        email?: string
    }>
}

const usePatches = false

const leftEmptyPanelBuffer = 1
const rightEmptyPanelBuffer = 1
export const startingIndex = leftEmptyPanelBuffer

const debugHostPattern = /localhost(:[0-9=]+)?$/

function readPersistedDebugFlag() {
    if (typeof window === 'undefined') {
        return false
    }

    try {
        return window.localStorage?.getItem(LOCAL_STORAGE_DEBUG_KEY) === 'true'
    } catch (_) {
        return false
    }
}

function debugEnvironmentEnabled() {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return false
    }

    return (
        window.gon?.env === 'development' || Boolean(document.location.host.match(debugHostPattern))
    )
}

function storeDebugFlagEnabled() {
    try {
        return Boolean(useStore.getState().DEBUG)
    } catch (_) {
        return readPersistedDebugFlag()
    }
}

const knovStore = (set, get): IKnovStore => {
    const origSet = set

    function lockQuest(questId: number) {
        // console.log('>>> Locked quest ', questId)
        set(state => {
            state.lockedQuests.push(Number(questId))
        })
    }

    function unlockQuest(questId: number) {
        // console.log('>>> UnLocked quest ', questId)
        set(state => {
            state.lockedQuests = state.lockedQuests.filter(qid => Number(qid) !== Number(questId))
        })
    }

    function lockAnswer(answerId: number) {
        // console.log('>>> Locked answer ', answerId)
        set(state => {
            state.lockedAnswers.push(Number(answerId))
        })
    }

    function unlockAnswer(answerId: number) {
        // console.log('>>> UnLocked answer ', answerId)
        set(state => {
            state.lockedAnswers = state.lockedAnswers.filter(
                aid => Number(aid) !== Number(answerId),
            )
        })
    }

    set = (funcOrObj, callback) => {
        let out
        if (usePatches) {
            if (typeof funcOrObj === 'function') {
                out = produceWithPatches(draftState => funcOrObj(draftState, current))
            } else if (typeof funcOrObj === 'object') {
                out = produceWithPatches(draftState => ({ ...draftState, ...funcOrObj }))
            } else {
                console.warn('The parameter passed to store set function is incompatible type')
            }

            // const [nextState, patches] = out

            origSet(draftState => {
                let [nextState, patches] = out(draftState)
                // NOTE: all the Number(id) stuff below is necessary for these to
                // evaluate to true for js-footgun reasons
                if (
                    patches.find(
                        ({ path: [type, id] }) =>
                            (type === 'quests' && draftState.lockedQuests.includes(id)) ||
                            (type === 'answers' && draftState.lockedAnswers.includes(id)),
                    )
                ) {
                    patches = patches.filter(
                        ({ path: [type, id] }) =>
                            !(type === 'quests' && draftState.lockedQuests.includes(id)) &&
                            !(type === 'answers' && !draftState.lockedAnswers.includes(id)),
                    )
                    nextState = applyPatches(draftState, patches)
                    // console.log('🚻filtered patches: ', patches)
                }
                return origSet(nextState)
            })
        } else {
            let nextState
            if (typeof funcOrObj === 'function') {
                nextState = produce(funcOrObj)
            } else if (typeof funcOrObj === 'object') {
                nextState = produce(draftState => ({ ...draftState, ...funcOrObj }))
            } else {
                console.warn('The parameter passed to store set function is incompatible type')
            }

            origSet(nextState)
        }
        if (callback) {
            callback()
        }
    }

    const getSpaceId = () =>
        get().activeSpaceId || gon.currentUser?.space_id || gon.KNOVIGATOR_SPACE_ID

    const getSpace = spaceId => get().userSpaces?.find(space => space.id === spaceId)
    const getActiveSpace = () => getSpace(get().activeSpaceId)

    const setActiveSpace = (spaceId: string, space: Space) => {
        if (gon.currentUser) {
            // TODO make this a partial State type.
            let newSpaceState: {
                activeSpaceId: string
                middleFilter: {}
                loadingSpaceId: string | null
                userSpaces?: Space[]
                questHistory: Quest[]
                starredQuests: Quest[]
                middleQuestId: string
                middleQuest: Quest
            } = {
                activeSpaceId: spaceId,
                middleFilter: { public: true },
                loadingSpaceId: spaceId,
                questHistory: [],
                starredQuests: [],
                middleQuestId: null,
                middleQuest: null,
            }
            if (space) newSpaceState.userSpaces = [...get().userSpaces, space]

            set({
                loadingSpaceId: spaceId,
            })

            // Keep space in sync with server user as we switch.
            // TODO remove space_id from server user and rely on client param.
            api.updateUser(gon.currentUser.id, { space_id: spaceId }).then(() => {
                set(newSpaceState)
            })

            /*
            const newSubdomain = space.subdomain_safe_name
            let newUrl = new URL(location.href)
            let splitHostname = newUrl.hostname.split('.')
            if (splitHostname.length > 1) splitHostname.shift()
            splitHostname.unshift(newSubdomain)
            newUrl.hostname = splitHostname.join('.')
            api.updateUser(gon.currentUser.id, { space_id: spaceId }).then(() => {
                location.href = newUrl.toString()
            })
            */
        }
    }

    const setActiveHistoryTab = (tab: 'history' | 'starred') => {
        set({ activeHistoryTab: tab })
        api.updateUserOptions({ activeHistoryTab: tab })
    }

    const setUserSpaces = spaces => set({ userSpaces: spaces })
    const setUserStreams = streams => set({ userStreams: streams })

    const updateQuest = (quest: Quest) => {
        cache.cacheQuest(quest)
    }

    const getQuestState = questId => {
        const state = get()
        if (state.middleQuest && state.middleQuest.id === questId) return state.middleQuest
        // TODO separate middleQuests display, and the quests data it shows similar to rightQuests vs childQuests.
        if (state.middleQuests && state.middleQuests.some(q => q.id === questId))
            return state.middleQuests.find(q => q.id === questId)
        if (state.childQuests && state.childQuests.some(q => q.id === questId))
            return state.childQuests.find(q => q.id === questId)
        if (state.rightQuests && state.rightQuests.some(q => q.id === questId))
            return state.rightQuests.find(q => q.id === questId)

        if (state.rightQuest && state.rightQuest.id === questId) return state.rightQuest
        if (state.pinnedQuest && state.pinnedQuest.id === questId) return state.pinnedQuest
    }

    const updateQuestAnswer = (quest, answer) => {
        let newQuest
        if (quest && answer) {
            const parent = quest.parent
            if (parent?.id === answer.id) {
                newQuest = { ...quest, parent: answer }
            } else {
                const ix = quest.sorted_answers.findIndex(ans => ans.id === answer.id)
                let newAnswers
                if (ix > -1) {
                    newAnswers = [...quest.sorted_answers]
                    newAnswers[ix] = answer
                } else {
                    newAnswers = [...quest.sorted_answers, answer]
                }
                newQuest = { ...quest, sorted_answers: newAnswers }
            }
        }
        return newQuest
    }

    const pushUnactedIds = ids => {
        const state = get()
        set({
            unactedAnswers: Array.from(new Set([...state.unactedAnswers, ...ids])),
        })
    }

    const pushUnactedParentIds = ids => {
        const state = get()
        set({
            unactedParents: Array.from(new Set([...state.unactedParents, ...ids])),
        })
    }

    const deleteEmbed = async (quest, answer, embedId) => {
        const newEmbeds = answer.embeds.filter(embed => embed.id !== embedId)
        const newAnswer = Object.assign({}, answer, { embeds: newEmbeds })
        const newQuest = updateQuestAnswer(quest, newAnswer)
        if (newQuest) {
            updateQuest(newQuest)
        }
        api.deleteEmbed(answer.id, embedId)
    }

    async function mergeAnswers(containerQuestId, embeddedQuestId) {
        const mergedQuest = await api.mergeAnswers(containerQuestId, embeddedQuestId)
        if (mergedQuest)
            // #TODO Remove from history.
            updateQuest(mergedQuest)
        actions.modalActions.closeModal()
    }

    async function setVideoDownloadInProgress(inProgress) {
        set({
            videoDownloadInProgress: inProgress,
        })
    }

    async function pinQuestToTeam(teamId, quest) {
        api.pinQuest(quest.id, teamId)
        const newTeam = Object.assign({}, quest.team, { quest_id: quest.id })
        const newQuest = Object.assign({}, quest, { team: newTeam })
        updateQuest(newQuest)
    }

    async function unpinQuestFromTeam(teamId, quest) {
        api.unpinQuest(quest.id, teamId)
        const newTeam = Object.assign({}, quest.team, { quest_id: null })
        const newQuest = Object.assign({}, quest, { team: newTeam })
        updateQuest(newQuest)
    }

    const actions: IKnovStore['actions'] = {
        modalActions: {
            openAuthModal: (
                mode: AuthModalMode = 'signup',
                source: AuthModalSource = 'unknown',
            ) => {
                window.logEv('OPEN_AUTH_MODAL', {
                    auth_mode: mode,
                    auth_surface: 'knov_modal',
                    trigger_source: source,
                })

                set({
                    isModalOpen: true,
                    modalParams: {
                        type: 'Auth',
                        mode,
                        authSource: source,
                        noHeader: true,
                        customStyles: {
                            width: 'min(92dvw, 760px)',
                            maxWidth: '760px',
                            minWidth: '320px',
                        },
                        mobileCustomStyles: {
                            width: '100dvw',
                            maxWidth: '100dvw',
                            minWidth: '0',
                            height: '100dvh',
                            maxHeight: '100dvh',
                            left: '0',
                            top: '0',
                            transform: 'none',
                            borderRadius: '0',
                        },
                    },
                })
            },

            openWalletModal: (params = {}) => {
                // Track wallet modal open
                window.logEv('BSV_WALLET_OPEN', {
                    trigger_source: 'user_action',
                })
                set({
                    isModalOpen: true,
                    modalParams: {
                        type: 'Wallet',
                        icon: 'WalletIcon',
                        customStyles: {
                            top: '0',
                            height: '100dvh',
                            maxHeight: '100dvh',
                            width: '100dvw',
                            maxWidth: '100dvw',
                            borderRadius: '0',
                        },
                        mobileCustomStyles: {
                            top: '0',
                            height: '100dvh',
                            maxHeight: '100dvh',
                            width: '100dvw',
                            maxWidth: '100dvw',
                            borderRadius: '0',
                        },
                        ...params,
                    },
                })
            },

            openDeviceTransferModal: (params = {}) => {
                set({
                    isModalOpen: true,
                    modalParams: {
                        type: 'DeviceTransfer',
                        title: 'Secure Device Transfer',
                        noHeader: true,
                        hideCloseButton: false,
                        customStyles: {
                            top: '0',
                            height: '100dvh',
                            maxHeight: '100dvh',
                            width: '100dvw',
                            maxWidth: '100dvw',
                            borderRadius: '0',
                            margin: '0',
                        },
                        mobileCustomStyles: {
                            top: '0',
                            height: '100dvh',
                            maxHeight: '100dvh',
                            width: '100dvw',
                            maxWidth: '100dvw',
                            borderRadius: '0',
                            margin: '0',
                        },
                        ...params,
                    },
                })
            },

            openOrdinalsModal: (ordinalId = null) => {
                set({
                    isModalOpen: true,
                    modalParams: {
                        type: 'OrdinalsViewer',
                        icon: 'OrdinalsIcon',
                        noHeader: 'true',
                        disableOverlayBlur: true,
                        shouldCloseOnOverlayClick: true,
                        customStyles: {
                            top: '0',
                            height: '100dvh',
                            maxHeight: '100dvh',
                            width: '100dvw',
                            maxWidth: '100dvw',
                            borderRadius: '0',
                            overflow: 'hidden',
                        },
                        ordinalId,
                    },
                })

                const url = ordinalId ? `/modal/ordinals/${ordinalId}` : '/modal/ordinals'
                window.history.pushState({}, '', url)
            },

            openLeaderboardModal: (category = 'given', date?: string) => {
                set({
                    isModalOpen: true,
                    modalParams: {
                        type: 'LeaderboardViewer',
                        title: 'Leaderboard',
                        icon: 'fa fa-trophy',
                        noHeader: true,
                        customStyles: {
                            top: '0',
                            height: '100dvh',
                            maxHeight: '100dvh',
                            width: '100dvw',
                            maxWidth: '100dvw',
                            borderRadius: '0',
                            margin: '0',
                        },
                        category,
                        date,
                    },
                })

                const dateQuery = date ? `?date=${encodeURIComponent(date)}` : ''
                window.history.pushState({}, '', `/modal/leaderboards/${category}${dateQuery}`)
            },

            openTdpViewerModal: () => {
                set({
                    isModalOpen: true,
                    modalParams: {
                        type: 'TDPViewer',
                        title: 'TDP',
                        icon: 'fa fa-bitcoin',
                        noHeader: true,
                        hideCloseButton: true,
                        customStyles: {
                            top: '0',
                            height: '100dvh',
                            maxHeight: '100dvh',
                            width: '100dvw',
                            maxWidth: '100dvw',
                            borderRadius: '0',
                            margin: '0',
                        },
                    },
                })

                window.history.pushState({}, '', '/modal/tdp')
            },

            openMetricsModal: () => {
                set({
                    isModalOpen: true,
                    modalParams: {
                        type: 'MetricsViewer',
                        title: 'Usage Metrics',
                        customStyles: {
                            top: '2.5vh',
                            width: '95vw',
                            minWidth: '95vw',
                            height: '95vh',
                            minHeight: '95vh',
                            margin: 'auto',
                        },
                    },
                })

                window.history.pushState({}, '', '/modal/metrics')
            },

            openHostDealModal: (params = {}) => {
                set({
                    isModalOpen: true,
                    modalParams: {
                        type: 'HostDeal',
                        noHeader: true,
                        sharePercent: params.sharePercent,
                        infoOnly: params.infoOnly,
                        customStyles: {
                            width: 'min(92dvw, 460px)',
                            minWidth: 'min(92dvw, 460px)',
                            maxWidth: '460px',
                        },
                    },
                })
            },

            openHostStreamPickerModal: (params = {}) => {
                set({
                    isModalOpen: true,
                    modalParams: {
                        type: 'HostStreamPicker',
                        noHeader: true,
                        sharePercent: params.sharePercent,
                        customStyles: {
                            width: 'min(460px, 92dvw)',
                            maxWidth: '460px',
                            borderRadius: '14px',
                        },
                    },
                })
            },

            openAdminModal: () => {
                set({
                    isModalOpen: true,
                    modalParams: {
                        type: 'AdminConsole',
                        noHeader: true,
                        // Full-bleed on every viewport. These must fully defeat the base
                        // modal content styles (centered, 50dvw, maxHeight 80dvh,
                        // marginRight -50%), which merge in before this object.
                        customStyles: {
                            position: 'fixed',
                            inset: '0',
                            top: '0',
                            left: '0',
                            right: '0',
                            bottom: '0',
                            width: '100dvw',
                            minWidth: '100dvw',
                            maxWidth: '100dvw',
                            height: '100dvh',
                            minHeight: '100dvh',
                            maxHeight: '100dvh',
                            borderRadius: '0',
                            border: 'none',
                            margin: '0',
                            marginRight: '0',
                            transform: 'none',
                            padding: '0',
                            overflow: 'hidden',
                        },
                    },
                })

                window.history.pushState({}, '', '/modal/admin')
            },

            openEditProfileModal: () => {
                set({
                    isModalOpen: true,
                    modalParams: {
                        type: 'EditProfile',
                        noHeader: true,
                        icon: null,
                        customStyles: {
                            top: '10vh',
                            height: '80dvh',
                            maxWidth: isMobile ? '95vw' : '50vw',
                        },
                    },
                })
            },

            openStreamSettingsModal: (team, panelId, filter) => {
                set({
                    isModalOpen: true,
                    modalParams: {
                        type: 'StreamSettings',
                        noHeader: true,
                        team,
                        panelId,
                        filter,
                        customStyles: {
                            top: '10vh',
                            width: '600px',
                            maxWidth: isMobile ? '95vw' : '600px',
                            height: '80vh',
                            maxHeight: '80vh',
                        },
                    },
                })
            },

            openCreateStreamModal: (panelId, filter) => {
                set({
                    isModalOpen: true,
                    modalParams: {
                        type: 'CreateStream',
                        panelId,
                        filter,
                        // Host-revenue design handoff: a 520px card (14px radius)
                        // on desktop, a 342px sheet with the top corners rounder
                        // than the bottom on mobile.
                        customStyles: {
                            width: 'min(520px, 92dvw)',
                            maxWidth: '520px',
                            borderRadius: '14px',
                        },
                        mobileCustomStyles: {
                            width: 'min(342px, 92dvw)',
                            maxWidth: '342px',
                            borderRadius: '18px 18px 14px 14px',
                        },
                    },
                })
            },

            openNewMusicModal: params => {
                set({
                    isModalOpen: true,
                    modalParams: {
                        type: 'NewMusic',
                        noHeader: true,
                        ...params,
                        // A CreateStream-sized card on desktop. On a phone it
                        // spans the width and starts just under the top safe
                        // area; KnovModal keeps it inside the safe areas.
                        customStyles: {
                            width: 'min(520px, 92dvw)',
                            maxWidth: '520px',
                            borderRadius: '14px',
                        },
                        mobileCustomStyles: {
                            width: '100dvw',
                            top: '12px',
                            borderRadius: '14px',
                        },
                    },
                })
            },

            openImportTextModal: async () => {
                set({
                    isModalOpen: true,
                    modalParams: {
                        type: 'ImportText',
                        title: 'Import Text',
                        icon: 'fa fa-download',
                    },
                })
            },

            openExportQuestModal: async quest => {
                set({
                    isModalOpen: true,
                    modalParams: {
                        type: 'ExportQuest',
                        title: 'Export Thread',
                        icon: 'fa fa-upload',
                        quest,
                    },
                })
            },

            openNewStreamModal: () => {
                set({
                    isModalOpen: true,
                    modalParams: {
                        type: 'NewStream',
                    },
                })
            },

            openNewSpaceModal: () => {
                set({
                    isModalOpen: true,
                    modalParams: {
                        type: 'NewSpace',
                    },
                })
            },

            openVideoRecorderModal: (quest, videoHandler, answer) => {
                set({
                    isModalOpen: true,
                    modalParams: {
                        type: 'VideoRecorder',
                        quest,
                        videoHandler,
                        answer,
                    },
                })
            },

            openScreenRecorderModal: (quest, videoHandler, answer) => {
                set({
                    isModalOpen: true,
                    modalParams: {
                        type: 'ScreenRecorder',
                        quest,
                        videoHandler,
                        answer,
                        hidden: true,
                    },
                })
            },

            openHandCashErrorModal: msg => {
                set({
                    isModalOpen: true,
                    modalParams: {
                        type: 'HandCashError',
                        message: msg,
                    },
                })
            },

            openMessageModal: msg => {
                set({
                    isModalOpen: true,
                    modalParams: {
                        type: 'Message',
                        message: msg,
                    },
                })
            },

            openConfirmModal: (message, confirmLabel, onConfirm, options = {}) => {
                const modalKey = options.modalKey
                const currentState = get()
                if (
                    modalKey &&
                    currentState.isModalOpen &&
                    currentState.modalParams?.modalKey === modalKey
                ) {
                    return
                }

                set({
                    isModalOpen: true,
                    modalParams: {
                        type: 'Confirm',
                        message,
                        onConfirm,
                        confirmLabel,
                        customStyles: {
                            maxHeight: '80vh',
                        },
                        ...options,
                    },
                })
            },

            openStreamImageUpdateModal: (team, callback) => {
                set({
                    isModalOpen: true,
                    modalParams: {
                        type: 'StreamImageUpdate',
                        team,
                        callback,
                    },
                })
            },

            openSpaceImageUpdateModal: (space, callback) => {
                set({
                    isModalOpen: true,
                    modalParams: {
                        type: 'SpaceImageUpdate',
                        space,
                        callback,
                    },
                })
            },

            openUserImageUpdateModal: (user, callback) => {
                set({
                    isModalOpen: true,
                    modalParams: {
                        type: 'UserImageUpdate',
                        user,
                        callback,
                    },
                })
            },

            closeModal: () => {
                const state = get()
                set({ isModalOpen: false })

                // Check if we're closing an OrdinalsViewer or MetricsViewer modal
                if (
                    state.modalParams?.type === 'OrdinalsViewer' ||
                    state.modalParams?.type === 'MetricsViewer' ||
                    state.modalParams?.type === 'AdminConsole' ||
                    state.modalParams?.type === 'LeaderboardViewer'
                ) {
                    // If current URL contains modal path, reset to home
                    const currentPath = window.location.pathname
                    if (currentPath.includes('/modal/')) {
                        window.history.pushState({}, '', '/')
                    }
                }

                // Notify any listening components that a NewAnswer modal was closed
                // so they can refresh their draft state from the store
                if (state.modalParams?.type === 'NewAnswer') {
                    const questId = (state.modalParams?.quest as { id?: string } | undefined)?.id
                    if (questId) {
                        window.dispatchEvent(
                            new CustomEvent('draft_modal_closed', { detail: { questId } }),
                        )
                    }
                }
            },

            closeCryptoModal: () => {
                set({
                    isCryptoPayModalOpen: false,
                    cryptoVoteValue: 0,
                    cryptoPayCallback: null,
                })
            },

            openCryptoModal(left, top, callback = null) {
                set({
                    isCryptoPayModalOpen: true,
                    cryptoPayModalLeft: left,
                    cryptoPayModalTop: top,
                    cryptoPayCallback: callback,
                })
            },

            openStoryModal(quest) {
                stopActiveMediaPlayback({ resetVideoToStart: true })
                setPlayThreadHash(true)
                set({
                    isStoryOpen: true,
                    isModalOpen: false, // so that modal doesn't show up in Story
                    modalParams: {
                        type: 'PlayAll',
                        quest,
                    },
                })
            },

            closeStoryModal() {
                setPlayThreadHash(false)
                set({ isStoryOpen: false })
            },
        },
        trayActions: {
            openConfirmTray: (message, confirmLabel, onConfirm, options = {}) => {
                set({
                    isTrayOpen: true,
                    trayParams: {
                        type: 'Confirm',
                        message,
                        onConfirm,
                        confirmLabel,
                        ...options,
                    },
                })
            },
            closeTray: () => {
                set({ isTrayOpen: false, trayParams: null })
            },
        },
    }

    const panels = {
        state: [],
        lastAction: null,
        animatedPanelInsert: null,
        animatedPanelRemove: null,
        rowShift: null,

        getPanels: () => get().panels.state,

        getPanel: panelId => get().panels.state.find(panel => panel.panelId === panelId),

        getIndexById: panelId => {
            return get().panels.state.findIndex(panel => panel.panelId === panelId)
        },

        getPanelToRight: panelId => {
            const { getIndexById, state } = get().panels
            const rightPanelIndex = getIndexById(panelId) + 1
            return state[rightPanelIndex]
        },

        getPanelToLeft: panelId => {
            const { getIndexById, state } = get().panels
            const rightPanelIndex = getIndexById(panelId) - 1
            return state[rightPanelIndex]
        },

        editPanel: (panelId, newPanelValues) => {
            const { getIndexById } = get().panels
            let index
            if (panelId !== undefined) {
                index = getIndexById(panelId)
            }
            set(draft => {
                if (typeof index !== 'number' || index < 0) return
                const oldPanel = draft.panels.state[index]
                if (!oldPanel) return

                // IMPORTANT: mutate the existing panel object instead of replacing it.
                // Replacing with `{ ...oldPanel, ...newPanelValues }` can reset/lose internal
                // per-panel state (e.g. savedScrollTop, refs) across filter/order changes.
                // An open-placement intent is scoped to the quest it was created for; a
                // quest switch invalidates it unless the caller provides a fresh one.
                if (
                    'filter' in newPanelValues &&
                    newPanelValues.filter?.questId !== oldPanel.filter?.questId &&
                    !('pendingOpenPlacement' in newPanelValues)
                ) {
                    oldPanel.pendingOpenPlacement = undefined
                    oldPanel.openPlacementAppliedKey = undefined
                }
                Object.assign(oldPanel, newPanelValues)

                // But we still need to trigger reactive updates for subscribers that rely on
                // referential equality of the panels array (or the panel object pointer changing).
                // Cloning the array preserves the PanelState instance while ensuring updates propagate.
                draft.panels.state = [...draft.panels.state]
            })
        },

        insertPanelAtIndex: (index, newPanel, actionParams = {}) => {
            const panelToInsert = new PanelState(newPanel)
            //console.log('insertPanelAtIndex', index, panelToInsert.panelId)
            set(draft => {
                draft.panels.state.splice(index, 0, panelToInsert)
                draft.panels.animatedPanelInsert = null
                draft.panels.lastAction = { ...actionParams, insert: panelToInsert }
            })
            return panelToInsert.panelId
        },

        removePanelAtIndex: removeIndex => {
            const panels = get().panels
            const panelToRemove = panels.state[removeIndex]
            const animated = panels.animatedPanelRemove
                ? panels.animatedPanelRemove.direction
                : false
            set(draft => {
                draft.panels.state = [
                    ...draft.panels.state.slice(0, removeIndex),
                    ...draft.panels.state.slice(removeIndex + 1),
                ]
                draft.panels.lastAction = {
                    remove: panelToRemove,
                    removeIndex,
                    animated,
                    centerIndex: getCenterIndex(),
                    panelIds: panels.state.map(panel => panel.panelId),
                }
                draft.panels.animatedPanelRemove = null
            })
        },

        // Same as removePanelAtIndex but marks the action to suppress slide effects
        removePanelAtIndexNoSlide: removeIndex => {
            const panels = get().panels
            const panelToRemove = panels.state[removeIndex]
            set(draft => {
                draft.panels.state = [
                    ...draft.panels.state.slice(0, removeIndex),
                    ...draft.panels.state.slice(removeIndex + 1),
                ]
                draft.panels.lastAction = {
                    remove: panelToRemove,
                    removeIndex,
                    animated: false,
                    centerIndex: getCenterIndex(),
                    panelIds: panels.state.map(panel => panel.panelId),
                    suppressSlide: true,
                }
                draft.panels.animatedPanelRemove = null
            })
        },

        startAnimatedInsert: (
            index,
            newPanel,
            options: { direction: 'left' | 'right'; actionParams?: object },
        ) => {
            //console.log('startAnimatedInsert', index, options.direction)
            // Desktop three-panel: insert now and glide every window from its old box
            // to its new one together. Deferring the insert behind a slide-out left the
            // slot empty, then popped the new window in once the slide had finished.
            // openCenter keeps its own choreography below.
            // openCenter inserts right of the centre and its slide effect moves the view
            // one panel on in the same commit (useSlideEffects), so the left windows
            // glide out while the new centre fades in and the right window holds.
            const actionParams = (options.actionParams || {}) as Record<string, unknown>
            if (!isMobile && get().numPanels === 3) {
                const boxes = capturePanelRow()
                const panelToInsert = new PanelState(newPanel)
                const insertIndex = (actionParams.insertAtIndex as number | undefined) ?? index
                set(draft => {
                    draft.panels.state.splice(insertIndex, 0, panelToInsert)
                    draft.panels.animatedPanelInsert = null
                    draft.panels.lastAction = { ...actionParams, insert: panelToInsert }
                    draft.panels.rowShift = {
                        token: nextPanelRowShiftToken(),
                        boxes,
                        enterFrom: actionParams.openCenter ? 'right' : options.direction,
                    }
                })
                return
            }
            set(state => {
                state.panels.animatedPanelInsert = {
                    index,
                    panel: newPanel,
                    direction: options.direction,
                    actionParams: options.actionParams,
                }
            })
        },

        completeAnimatedInsert: () => {
            const {
                panels: { insertPanelAtIndex, animatedPanelInsert },
            } = get()
            // Preserve action params before insertion mutates state
            const actionParams = (animatedPanelInsert?.actionParams || {}) as Record<
                string,
                unknown
            >

            const insertIndex =
                (actionParams?.insertAtIndex as number | undefined) ?? animatedPanelInsert.index
            const newPanel = animatedPanelInsert.panel

            insertPanelAtIndex(insertIndex, newPanel, actionParams)

            // Post-insert chaining: support openCenter flow without relying on slide effects
            if (actionParams?.openCenter) {
                const removeLeftIndex = actionParams.removeLeftIndex as number | undefined
                const animateRemove: boolean = !!actionParams.animateRemove

                if (typeof removeLeftIndex === 'number') {
                    if (animateRemove) {
                        // Animate removal if requested (will trigger transition → completeAnimatedRemove)
                        panels.startAnimatedRemove(removeLeftIndex, 'left')
                    } else {
                        // Instant removal without slide effects; we'll imperatively adjust swiper
                        panels.removePanelAtIndexNoSlide(removeLeftIndex)
                    }
                }

                // Imperatively advance swiper's left-of-center index by +1 for openCenter
                // so that activeIndex 0 → 1 on the initial [empty, center, empty] baseline.
                try {
                    const swiper = getSwiperRef()?.current
                    if (swiper) {
                        const prevActiveIndex =
                            (actionParams?.prevActiveIndex as number | undefined) ??
                            swiper.activeIndex
                        const isOne = isMobile || get().numPanels === 1
                        const target = isOne ? prevActiveIndex : prevActiveIndex + 1
                        if (DEBUG())
                            console.log('OPENCENTER_STABILIZE/begin', {
                                prevActiveIndex,
                                isOne,
                                target,
                                slidesPerView: swiper.params?.slidesPerView,
                                slides: swiper.slides?.length,
                            })
                        requestAnimationFrame(() => {
                            try {
                                swiper.update && swiper.update()
                                swiper.updateSlides && swiper.updateSlides()
                                swiper.updateSlidesClasses && swiper.updateSlidesClasses()
                                swiper.updateProgress && swiper.updateProgress()
                                swiper.virtual &&
                                    swiper.virtual.update &&
                                    swiper.virtual.update(true)
                            } catch {}
                            requestAnimationFrame(() => {
                                try {
                                    swiper.slideTo(target, 0)
                                } catch (e) {
                                    if (DEBUG())
                                        console.warn('OPENCENTER_STABILIZE/slideTo error', e)
                                }
                                // Fallback: sync store indices in case callbacks are skipped
                                const storeCenter = isOne ? target : target + 1
                                get().set({ activeIndex: target, centerIndex: storeCenter })
                                // After indices settle, apply transforms based on the new center
                                try {
                                    const centerIdxNow = getCenterIndex()
                                    const listNow = get().panels.state
                                    const c = listNow[centerIdxNow]
                                    const l = listNow[centerIdxNow - 1]
                                    const r = listNow[centerIdxNow + 1]
                                    requestAnimationFrame(() => {
                                        if (c) transformTo(BIG, c, 'none')
                                        if (l) transformTo(SMALL, l, 'none')
                                        if (r) transformTo(SMALL, r, 'none')
                                    })
                                } catch {}
                                if (DEBUG()) {
                                    const after = getSwiperRef()?.current?.activeIndex
                                    console.log('OPENCENTER_STABILIZE/done', {
                                        after,
                                        storeActiveIndex: get().activeIndex,
                                        storeCenterIndex: get().centerIndex,
                                    })
                                }
                            })
                        })
                    }
                } catch {}
            }
        },

        startAnimatedRemove: (index, direction) => {
            // Desktop three-panel: remove now and glide the remaining windows into
            // place. The deferred removal hid the window at once, slid the neighbours
            // at the wrong size, then snapped them (slot reuse swapped their scales).
            // `animated` keeps useSlideEffects' view adjustment for the direction.
            if (!isMobile && get().numPanels === 3) {
                const boxes = capturePanelRow()
                const { state: panelState } = get().panels
                const panelToRemove = panelState[index]
                set(draft => {
                    draft.panels.state = [
                        ...draft.panels.state.slice(0, index),
                        ...draft.panels.state.slice(index + 1),
                    ]
                    draft.panels.lastAction = {
                        remove: panelToRemove,
                        removeIndex: index,
                        animated: direction,
                        centerIndex: getCenterIndex(),
                        panelIds: panelState.map(panel => panel.panelId),
                    }
                    draft.panels.animatedPanelRemove = null
                    draft.panels.rowShift = {
                        token: nextPanelRowShiftToken(),
                        boxes,
                        // The row moves toward `direction`; what comes into view
                        // arrives from the other side.
                        enterFrom: direction === 'left' ? 'right' : 'left',
                    }
                })
                return
            }
            set(draft => {
                draft.panels.animatedPanelRemove = { index, direction }
            })
        },

        completeAnimatedRemove: () => {
            const {
                panels: { animatedPanelRemove },
            } = get()
            panels.removePanelAtIndex(animatedPanelRemove.index)
        },

        insertPanelRight: (
            panelId,
            newPanel,
            options: { animate: boolean; actionParams?: object } = { animate: false },
        ) => {
            const {
                panels: {
                    getIndexById,
                    insertPanelAtIndex,
                    state: panelState,
                    startAnimatedInsert,
                },
            } = get()
            const currentIndex = getIndexById(panelId)
            const nextPanel = panelState[currentIndex + 1]
            if (nextPanel?.empty) {
                const targetPanelId = newPanel.panelId || nextPanel.panelId
                // The filled slot's window is new content: no old box, so it fades in.
                let rowShiftBoxes: Record<string, PanelBox> | null = null
                if (!isMobile && get().numPanels === 3) {
                    rowShiftBoxes = capturePanelRow()
                    delete rowShiftBoxes[nextPanel.panelId]
                }
                set(draft => {
                    const targetPanel = draft.panels.state[currentIndex + 1]
                    if (!targetPanel?.empty) return

                    Object.assign(targetPanel, newPanel, {
                        panelId: targetPanelId,
                        empty: false,
                        savedScrollTop: newPanel.savedScrollTop ?? 0,
                        deferContentUntilTransition: newPanel.deferContentUntilTransition ?? false,
                        pendingOpenPlacement: newPanel.pendingOpenPlacement ?? undefined,
                        openPlacementAppliedKey: undefined,
                    })

                    if (
                        !isMobile &&
                        get().numPanels !== 1 &&
                        currentIndex + 1 === draft.panels.state.length - 1
                    ) {
                        draft.panels.state.push(new PanelState({ empty: true }))
                    }

                    draft.panels.animatedPanelInsert = null
                    draft.panels.lastAction = {
                        ...(options?.actionParams || {}),
                        insert: targetPanel,
                    }
                    // A window filling an empty side slot fades in rather than popping.
                    if (rowShiftBoxes) {
                        draft.panels.rowShift = {
                            token: nextPanelRowShiftToken(),
                            boxes: rowShiftBoxes,
                            enterFrom: 'right',
                        }
                    }
                    draft.panels.state = [...draft.panels.state]
                })
                return targetPanelId
            }

            if (
                !newPanel.filter?.questId ||
                newPanel.filter?.questId !== nextPanel?.filter?.questId
            ) {
                const appendToEnd = currentIndex === panelState.length - rightEmptyPanelBuffer
                const requiresAnimatedInsert = options.animate && !appendToEnd
                if (requiresAnimatedInsert) {
                    startAnimatedInsert(currentIndex + 1, newPanel, {
                        direction: 'right',
                        actionParams: options?.actionParams,
                    })
                    return newPanel.panelId
                }

                return insertPanelAtIndex(currentIndex + 1, newPanel, options?.actionParams)
            }
            // Same-quest reuse: carry the retarget through so the route-owned reveal fires
            // and an at-rest container can still place pre-paint (intent survives only when
            // the caller provided one, which requires instant_reply_nav).
            if (nextPanel && newPanel.filter?.answerId && userHasFeature('instant_reply_nav')) {
                get().panels.editPanel(nextPanel.panelId, {
                    filter: { ...nextPanel.filter, answerId: newPanel.filter.answerId },
                    ...(newPanel.pendingOpenPlacement
                        ? { pendingOpenPlacement: newPanel.pendingOpenPlacement }
                        : {}),
                })
            }
            return nextPanel?.panelId
        },

        insertPanelLeft: (panelId, newPanel, options = { animate: false }) => {
            const {
                panels: { getIndexById, insertPanelAtIndex, startAnimatedInsert },
            } = get()
            const currentIndex = getIndexById(panelId)
            // Desktop one-panel: the new panel slides in from the left like Back. An
            // animated insert has no row to shift here and only stalled, then swapped.
            if (!isMobile && get().numPanels === 1) {
                insertPanelAtIndex(currentIndex, newPanel, {
                    slideInFromLeft: options.animate || undefined,
                })
                return
            }
            const prependToStart = currentIndex === 0
            const requiresAnimatedInsert = options.animate && !prependToStart
            requiresAnimatedInsert
                ? startAnimatedInsert(currentIndex, newPanel, { direction: 'left' })
                : insertPanelAtIndex(currentIndex, newPanel)
        },

        clearPanel: panelId => {
            panels.editPanel(panelId, {
                empty: true,
                newThread: false,
                stream: undefined,
                filter: undefined,
                questId: undefined,
                query: undefined,
                savedScrollTop: 0,
                deferContentUntilTransition: false,
                pendingOpenPlacement: undefined,
                openPlacementAppliedKey: undefined,
            })
        },

        appendEmptyPanel: () => {
            if (isMobile || get().numPanels === 1) return
            const newEmptyPanel = new PanelState({ empty: true })
            set(draft => {
                // This will activate the correct slide effect in useSlideEffects.
                draft.panels.lastAction = {
                    insert: draft.panels.state[draft.panels.state.length - 1],
                }
                draft.panels.state.push(newEmptyPanel)
            })
        },

        prependEmptyPanel: () => {
            if (isMobile || get().numPanels === 1) return
            const newEmptyPanel = new PanelState({ empty: true })
            set(draft => {
                // This will activate the correct slide effect in useSlideEffects.
                draft.panels.lastAction = { insert: draft.panels.state[0] }
                draft.panels.state.unshift(newEmptyPanel)
            })
        },

        appendSession: (newFilter, panelProps = {}) => {
            const panels = get().panels
            const numPanels = panels.getPanels().length
            const first = new PanelState({
                filter: newFilter,
                empty: false,
                ...panelProps,
            })
            const newPanels =
                isMobile || get().numPanels === 1
                    ? [first]
                    : [first, new PanelState({ empty: true })]
            set(draft => {
                draft.panels.state.splice(numPanels, 0, ...newPanels)
                // We're really inserting 2 panels, but the last one is empty, so we only want to show the first one.
                draft.panels.lastAction = { insert: newPanels[0], appendSession: true }
            })
        },
    }

    return {
        DEBUG: readPersistedDebugFlag(),
        panels: panels,
        isModalOpen: false,
        modalParams: { type: '' },
        isTrayOpen: false,
        trayParams: null,
        publicTeams: [],

        activeSpaceId: null,
        loadingSpaceId: null,
        userFeatures: null,
        /** utc time in ms since cache was hydrated from localstorage **/
        lastLocalCacheRead: 0,
        /** cached answers live here **/
        answers: {} as { [answerId: string]: Answer },
        /** cached quests live here **/
        quests: {} as { [questId: string]: Quest },
        users: [],
        userSpaces: [],
        userStreams: [],
        lockedQuests: [] as string[],
        lockedAnswers: [] as string[],
        lockQuest,
        unlockQuest,
        lockAnswer,
        unlockAnswer,
        sliding: false,
        slidingBack: false,
        middlePanelExpandedQuests: {},
        rightPanelExpandedQuests: {},
        selectedAnswers: {} as { [panelId: string]: { answerId: string } | null },
        searchHeaderAnimationStates: {} as {
            [panelId: string]: { state?: 'fadeIn' | 'fadeOut' | string }
        },
        selectedQuote: null,
        selectedQuoteParentAnswer: null,
        hideMiddlePanelExcept: null,
        hideRightPanelExcept: null,
        middleNewQuestInput: '',
        newAnswerQueryMiddle: null,
        newAnswerQueryRight: null,
        unactedAnswers: [],
        unactedParents: [],
        starredQuests: [],
        starredQuestIds: {} as { [questId: string]: boolean },
        pinnedTeamIds: {} as { [teamId: string]: boolean },
        draftQuestIds: { middle: null, right: null } as { [panel: string]: string },
        currentUserBsvWalletType: null as BsvWalletType,
        middleQuest: null as Quest | null,
        middleQuests: [] as Quest[],

        upvalueSats: null,
        upvalueUsd: null,
        seedSats: null,
        seedUsd: null,
        boostSats: null,
        effectEnabled: true,

        answerJobs: {} as { [answerId: string]: JobStatus },
        bsvUnit: BITCOIN_UNIT,
        signalType: null,

        // Panel navigation state
        numNoti: 0,
        activeIndex: 0,
        onePanelPrewarmIndex: null,
        onePanelPrewarmVisible: false,
        centerIndex: 0,
        questHistory: [],
        knovigatorSpace: null,
        publicUser: null,

        // Getter Setters.
        showSidebar: false,
        setShowSidebar: (val: boolean, options?: { persist?: boolean }) => {
            const prev = get().showSidebar
            if (prev === val) return

            set(state => {
                state.showSidebar = val
            })

            const persist = options?.persist !== undefined ? options.persist : true
            const shouldPersist = persist && !isMobile

            if (shouldPersist) {
                const optionValue = val ? 'open' : 'close'
                void api.updateUserSpaceOptions({ sidebarOpen: optionValue }).catch(error => {
                    console.error('Failed to persist sidebarOpen option', error)
                })

                if (gon?.currentUser?.space_options) {
                    gon.currentUser.space_options.sidebarOpen = optionValue
                }
            }
        },
        closeSidebarOnBlur: false,
        setCloseSidebarOnBlur: (close: boolean) => set({ closeSidebarOnBlur: close }),
        closeSidebarOnSwipe: isMobile,
        setCloseSidebarOnSwipe: (close: boolean) => set({ closeSidebarOnSwipe: close }),
        showRightSidebar: false,
        setShowRightSidebar: show => set({ showRightSidebar: show }),
        numPanels: DEFAULT_NUM_PANELS,
        setNumPanels: (numPanels: number) => set({ numPanels }),
        getSpace,
        getActiveSpace,
        setActiveSpace,
        setActiveHistoryTab,
        setUserSpaces,
        setUserStreams,
        set: set,
        get: get,
        getSpaceId,
        deleteEmbed,
        updateQuest,
        getQuestState,
        updateQuestAnswer,
        mergeAnswers,
        setVideoDownloadInProgress,
        pinQuestToTeam,
        unpinQuestFromTeam,
        actions,
        pushUnactedIds,
        pushUnactedParentIds,
        aiModels: [],
        actionDefinitions: [],
        exchangeRate: 0,
        agentModelConfig: null,
        pendingUpvaluesByAnswer: {},
        upvalueQueueStatus: {},
        enqueuePendingUpvalue: (answerId: string, op: PendingUpvalueOp) =>
            set(state => {
                if (!state.pendingUpvaluesByAnswer[answerId]) {
                    state.pendingUpvaluesByAnswer[answerId] = []
                }
                state.pendingUpvaluesByAnswer[answerId].push(op)
            }),
        clearPendingUpvalues: (answerId: string, opIds: string[]) =>
            set(state => {
                if (!state.pendingUpvaluesByAnswer[answerId]) {
                    return
                }
                state.pendingUpvaluesByAnswer[answerId] = state.pendingUpvaluesByAnswer[
                    answerId
                ].filter(op => !opIds.includes(op.id))
                if (state.pendingUpvaluesByAnswer[answerId].length === 0) {
                    delete state.pendingUpvaluesByAnswer[answerId]
                }
            }),
        setUpvalueQueueStatus: (answerId: string, status: 'idle' | 'processing') =>
            set(state => {
                state.upvalueQueueStatus[answerId] = status
                if (status === 'idle' && !(state.pendingUpvaluesByAnswer[answerId]?.length > 0)) {
                    delete state.upvalueQueueStatus[answerId]
                }
            }),
        pendingOptimisticAnswersByQuest: {},
        trackPendingOptimisticAnswer: (questId: string, answerId: string) =>
            set(state => {
                const qid = String(questId)
                const aid = String(answerId)
                const list = state.pendingOptimisticAnswersByQuest[qid] || []
                const existingIx = list.findIndex(ref => String(ref.answerId) === aid)
                const nextRef = { answerId: aid, createdAtMs: Date.now() }
                if (existingIx >= 0) {
                    list[existingIx] = nextRef
                } else {
                    list.push(nextRef)
                }
                state.pendingOptimisticAnswersByQuest[qid] = list
            }),
        resolvePendingOptimisticAnswer: (questId: string, answerId: string) =>
            set(state => {
                const qid = String(questId)
                const aid = String(answerId)
                const list = state.pendingOptimisticAnswersByQuest[qid]
                if (!list?.length) return
                const next = list.filter(ref => String(ref.answerId) !== aid)
                if (next.length) state.pendingOptimisticAnswersByQuest[qid] = next
                else delete state.pendingOptimisticAnswersByQuest[qid]
            }),
        globalNotice: null,
        channelJoinInvite: null,
        showGlobalNotice: (message, options = {}) =>
            set(state => {
                state.globalNotice = {
                    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
                    message,
                    detail: options.detail,
                    variant: options.variant || 'info',
                    durationMs: options.durationMs || 4500,
                    kind: options.kind,
                    replaceKind: options.replaceKind,
                    testId: options.testId,
                    actionLabel: options.actionLabel,
                    cancelLabel: options.cancelLabel,
                    onAction: options.onAction,
                }
            }),
        updateGlobalNotice: (kind, message, options = {}) =>
            set(state => {
                const current = state.globalNotice
                if (!current || current.kind !== kind) return
                current.message = message
                if (options.detail !== undefined) current.detail = options.detail
                if (options.variant) current.variant = options.variant
                // Give the resolved (success/error) toast a finite auto-dismiss even though
                // the pending one had none.
                current.durationMs =
                    options.durationMs ?? (options.variant === 'error' ? 9000 : 4500)
                if (options.testId) current.testId = options.testId
                if (options.actionLabel !== undefined) current.actionLabel = options.actionLabel
                if (options.cancelLabel !== undefined) current.cancelLabel = options.cancelLabel
                if (options.onAction !== undefined) current.onAction = options.onAction
            }),
        clearGlobalNotice: id =>
            set(state => {
                if (!id || state.globalNotice?.id === id) {
                    state.globalNotice = null
                }
            }),
        appearanceTheme: DEFAULT_APPEARANCE_THEME,
        setAppearanceTheme: (newTheme: AppearanceTheme) => set({ appearanceTheme: newTheme }),
    }
}

if (usePatches) enablePatches()

const useStore = createWithEqualityFn(knovStore, shallow)

if (typeof window !== 'undefined') {
    window.showSignupModal = (source = 'legacy_signup_link') => {
        useStore.getState().actions.modalActions.openAuthModal('signup', source)
        return true
    }

    window.showLoginModal = (source = 'legacy_login_link') => {
        useStore.getState().actions.modalActions.openAuthModal('login', source)
        return true
    }
}

/** Global developer-access flag used for console helpers and debug-only API
 *  exposure. This is intentionally broader than the UI debug flag stored in
 *  `state.DEBUG`, which stays opt-in. */
export function DEBUG() {
    return debugEnvironmentEnabled() || storeDebugFlagEnabled()
}

function syncDebugConsoleGlobals() {
    const debugEnabled = DEBUG()

    if (debugEnabled) {
        /* some occasionally useful debug functions for interacting with the
         * app/cache from the dev tools console*/
        window.knovStore = useStore
        window.knovSet = window.knovStore.set
        window.knovGet = window.knovStore.get
        window.getCachedAnswer = id => cache.getCachedAnswer(id)
        window.getCachedQuest = id => cache.getCachedQuest(id)

        // usage: updateCachedQuest(248, {sorted_answers: []})
        window.updateCachedQuest = (...p: [number, any]) => cache.updateCachedQuest(...p)

        // usage: updateCachedAnswer(12, {position: 2})
        window.updateCachedAnswer = (...p: [number, any]) => cache.updateCachedAnswer(...p)

        // update the text content and the delta json in one go for easy visibility
        // usage: updateAnswerContent(22, "answer 22 now says this")
        window.updateAnswerContent = (ansId, newContent) =>
            window.updateAnswer(ansId, {
                content: newContent,
                delta_json: {
                    ops: [
                        {
                            insert: newContent,
                        },
                    ],
                },
            })
    } else {
        delete window.knovStore
        delete window.knovSet
        delete window.knovGet
        delete window.getCachedAnswer
        delete window.getCachedQuest
        delete window.updateCachedQuest
        delete window.updateCachedAnswer
        delete window.updateAnswerContent
    }

    window.dispatchEvent(
        new CustomEvent(DEBUG_MODE_CHANGED_EVENT, {
            detail: { debugEnabled },
        }),
    )
}

window.setDebug = (debug = false, persist = false) => {
    const { set } = useStore.getState()
    set(state => {
        state.DEBUG = debug
    })
    if (persist) {
        localStorage.setItem(LOCAL_STORAGE_DEBUG_KEY, debug.toString())
    }
    syncDebugConsoleGlobals()
}

syncDebugConsoleGlobals()
export const knovHostName = window.gon?.host || 'app.knovigator.com'

export default useStore
